import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Building2, Camera, Check, ChevronLeft, Clock, MapPin, XCircle, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DeliveryOrder } from "@/features/delivery/api/deliveryApi";
import { buildDeliverySummary, formatDeliveryStatus } from "@/features/delivery/lib/deliveryCalculations.js";
import { clearDriverToken, driverOrderAction, getDriverOrder, getDriverToken, isUnauthenticated } from "@/features/driver/api/driverApi";
import { buildMapLinks, formatScheduledDateTime, formatStationAddress, formatStationName } from "@/features/driver/lib/driverPresentation.js";
import { DriverLanguageProvider, useDriverLanguage } from "@/features/driver/i18n/useDriverLanguage";
import CameraCapture from "@/features/driver/components/CameraCapture";
import WaitingTimer from "@/features/driver/components/WaitingTimer";
import JobSummary from "@/features/driver/components/JobSummary";
import ContactButtons from "@/features/driver/components/ContactButtons";
import DriverMenu from "@/features/driver/components/DriverMenu";

type PhotoAction = "arrived" | "complete" | "collected" | "no-show";

// The driver `show` endpoint eager-loads the reservation; expose the phone
// loosely without coupling the shared DeliveryOrder type to reservation shape.
type DriverOrder = DeliveryOrder & {
  reservation?: { mobile_no?: string | null; customer?: { mobile_no?: string | null } } | null;
};

function customerPhone(order: DriverOrder | null): string | null {
  return order?.reservation?.customer?.mobile_no ?? order?.reservation?.mobile_no ?? null;
}

export default function DriverOrderDetail() {
  return (
    <DriverLanguageProvider>
      <DriverOrderDetailContent />
    </DriverLanguageProvider>
  );
}

function DriverOrderDetailContent() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useDriverLanguage();
  const [order, setOrder] = useState<DriverOrder | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cameraAction, setCameraAction] = useState<PhotoAction | null>(null);
  const summary = useMemo(() => buildDeliverySummary(order ?? undefined), [order]);

  useEffect(() => {
    if (!getDriverToken()) {
      navigate("/driver/login");
      return;
    }

    getDriverOrder(id)
      .then((result) => setOrder(result.order as DriverOrder))
      .catch((err) => {
        if (isUnauthenticated(err)) {
          clearDriverToken();
          navigate("/driver/login");
          return;
        }
        setError(err?.message ?? t("defaultLoadError"));
      });
  }, [id, navigate, t]);

  const runAction = async (action: "arrived" | "start-waiting" | "start-driving" | "complete" | "collected" | "returned-to-branch" | "no-show", body: Record<string, unknown> = {}) => {
    if (!order) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const result = await driverOrderAction(order.id, action, body);
      setOrder(result.order as DriverOrder);
    } catch (err: any) {
      if (isUnauthenticated(err)) {
        clearDriverToken();
        navigate("/driver/login");
        return;
      }
      setError(err?.message ?? t("defaultUpdateError"));
    } finally {
      setIsLoading(false);
    }
  };

  const startWaiting = async (forceEarly = false) => {
    if (!order) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const result = await driverOrderAction(order.id, "start-waiting", forceEarly ? { force_early: true } : {});
      setOrder(result.order as DriverOrder);
    } catch (err: any) {
      if (err?.error_code === "WAITING_TOO_EARLY") {
        if (window.confirm(`${err.message ?? "It's before the scheduled time."}\n\nStarting early will be flagged to the office.`)) {
          await startWaiting(true);
        }
        return;
      }
      if (isUnauthenticated(err)) {
        clearDriverToken();
        navigate("/driver/login");
        return;
      }
      setError(err?.message ?? t("defaultUpdateError"));
    } finally {
      setIsLoading(false);
    }
  };

  const onPhotoCaptured = (dataUrl: string, coords: { lat: number; lng: number } | null) => {
    const action = cameraAction;
    setCameraAction(null);
    if (action) {
      void runAction(action, { photo: dataUrl, lat: coords?.lat, lng: coords?.lng });
    }
  };

  if (!order) {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center overflow-x-hidden bg-neutral-100 p-4 text-sm text-neutral-600">
        {error || t("loadingJob")}
      </main>
    );
  }

  const cameraTitle = cameraAction === "complete"
    ? t("handoverPhotoTitle")
    : cameraAction === "collected"
      ? t("collectionPhotoTitle")
      : cameraAction === "no-show"
        ? t("noShowProofLabel")
        : t("arrivalPhotoTitle");

  return (
    <main className="min-h-[100dvh] overflow-x-hidden bg-neutral-100">
      {cameraAction && (
        <CameraCapture
          order={order}
          title={cameraTitle}
          onCapture={onPhotoCaptured}
          onCancel={() => setCameraAction(null)}
        />
      )}

      <header className="sticky top-0 z-10 bg-europcarGreen px-4 pb-3 text-white [padding-top:max(0.75rem,env(safe-area-inset-top))]">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Link
              to="/driver/dashboard"
              aria-label={t("backToJobsAria")}
              className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-black/10"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0">
              <span className="inline-block rounded bg-black/20 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide">
                {formatDeliveryStatus(order.status)}
              </span>
              <p className="mt-1 flex items-center gap-1.5 truncate text-base font-bold leading-tight text-white">
                <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
                {formatScheduledDateTime(order.scheduled_at)}
              </p>
            </div>
          </div>
          <DriverMenu />
        </div>
      </header>

      <section className="mx-auto max-w-3xl space-y-4 px-4 py-5 [padding-bottom:max(1.25rem,env(safe-area-inset-bottom))]">
        {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}

        <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4 shadow-sm" data-testid="job-station">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-800">
              <Building2 className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <span className="block text-xs font-semibold uppercase tracking-wide text-blue-700">
                {order.type === "pickup" ? t("pickupStationLabel") : t("returnStationLabel")}
              </span>
              <p className="mt-0.5 break-words font-semibold text-neutral-950">
                {formatStationName(order.station) || t("stationUnavailable")}
              </p>
              {order.station && (() => {
                const stationAddress = formatStationAddress(order.station);
                const stationLat = order.station.rw_latitude ? Number(order.station.rw_latitude) : Number.NaN;
                const stationLng = order.station.rw_longitude ? Number(order.station.rw_longitude) : Number.NaN;
                const stationMapLinks = buildMapLinks(stationAddress || formatStationName(order.station), {
                  lat: stationLat,
                  lng: stationLng,
                });

                return stationAddress && stationMapLinks ? (
                  <a
                    href={stationMapLinks.googleMaps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-start gap-1.5 text-sm text-blue-800 underline decoration-blue-300 underline-offset-2"
                  >
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="break-words">{stationAddress}</span>
                  </a>
                ) : stationAddress ? (
                  <p className="mt-1 text-sm text-neutral-600">{stationAddress}</p>
                ) : null;
              })()}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
          {(() => {
            const mapLinks = buildMapLinks(order.customer_address, {
              lat: Number(order.customer_lat),
              lng: Number(order.customer_lng),
            });

            return mapLinks ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="-m-1 flex w-full items-start gap-2 rounded-md p-1 text-left transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
                  >
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-green-700" aria-hidden="true" />
                    <h2 className="break-words font-semibold text-green-800 underline decoration-green-300 underline-offset-2">
                      {order.customer_address}
                    </h2>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56 p-1.5">
                  <a
                    href={mapLinks.waze}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-100"
                  >
                    <Navigation className="h-4 w-4 text-sky-600" aria-hidden="true" /> {t("openInWaze")}
                  </a>
                  <a
                    href={mapLinks.googleMaps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-100"
                  >
                    <MapPin className="h-4 w-4 text-red-600" aria-hidden="true" /> {t("openInGoogleMaps")}
                  </a>
                  <a
                    href={mapLinks.appleMaps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-100"
                  >
                    <MapPin className="h-4 w-4 text-neutral-700" aria-hidden="true" /> {t("openInAppleMaps")}
                  </a>
                </PopoverContent>
              </Popover>
            ) : (
              <h2 className="break-words font-semibold text-neutral-900">{order.customer_address}</h2>
            );
          })()}
          {order.customer_display_name && (
            <p className="mt-1 text-sm text-neutral-700" data-testid="customer-name">
              {t("customerLabel")} <span className="font-medium">{order.customer_display_name}</span>
            </p>
          )}
          <p className="mt-1 text-sm text-neutral-600">{order.distance_km} km · {order.type === "pickup" ? t("pickupLabel") : t("returnLabel")}</p>
          <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
            <div className="min-w-0 rounded-md bg-neutral-50 p-3"><span className="block break-words leading-snug text-neutral-500">{t("deliveryLabel")}</span><strong>{summary.deliveryFee.toFixed(2)}</strong></div>
            <div className="min-w-0 rounded-md bg-neutral-50 p-3"><span className="block break-words leading-snug text-neutral-500">{t("afterHoursLabel")}</span><strong>{summary.afterHoursSurcharge.toFixed(2)}</strong></div>
            <div className="min-w-0 rounded-md bg-neutral-50 p-3"><span className="block break-words leading-snug text-neutral-500">{t("waitingLabel")}</span><strong>{summary.waitingFee.toFixed(2)}</strong></div>
          </div>
        </div>

        {["waiting", "driving"].includes(order.status) && (
          <WaitingTimer
            startedAt={order.status === "waiting" ? order.waiting_started_at : null}
            baseSeconds={order.waiting_seconds ?? 0}
          />
        )}

        {["completed", "no_show", "returned"].includes(order.status) && <JobSummary order={order} />}

        {(order.vehicle_plate || order.vehicle_model) && (
          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
            <span className="mb-1 block text-xs text-neutral-500">{t("vehicleLabel")}</span>
            <p className="font-semibold">{order.vehicle_model ?? ""}{order.vehicle_plate ? ` — ${order.vehicle_plate}` : ""}</p>
          </div>
        )}

        {!["completed", "no_show", "cancelled", "returned"].includes(order.status) && (
          <>
            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
              <span className="mb-2 block text-sm font-medium">{t("contactCustomerLabel")}</span>
              <ContactButtons phone={customerPhone(order)} />
            </div>

            {order.type === "pickup" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Button className="h-12 text-base" disabled={isLoading || order.status !== "assigned"} onClick={() => runAction("start-driving")}>
                  <Navigation className="mr-2 h-4 w-4" /> {t("startDrivingBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || !["en_route", "driving"].includes(order.status)} onClick={() => setCameraAction("arrived")}>
                  <Camera className="mr-2 h-4 w-4" /> {t("arrivedBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || !["arrived", "driving"].includes(order.status)} onClick={() => startWaiting()}>
                  <Clock className="mr-2 h-4 w-4" /> {order.status === "driving" ? t("resumeWaitingBtn") : t("startWaitingBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || order.status !== "waiting"} onClick={() => runAction("start-driving")}>
                  <Navigation className="mr-2 h-4 w-4" /> {t("pauseDrivingBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || !["arrived", "waiting", "driving"].includes(order.status)} onClick={() => setCameraAction("complete")}>
                  <Check className="mr-2 h-4 w-4" /> {t("completeHandoverBtn")}
                </Button>
                <Button className="h-12 text-base" variant="destructive" disabled={isLoading || !["arrived", "waiting", "driving"].includes(order.status)} onClick={() => setCameraAction("no-show")}>
                  <XCircle className="mr-2 h-4 w-4" /> {t("markNoShowBtn")}
                </Button>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Button className="h-12 text-base" disabled={isLoading || !["assigned", "en_route"].includes(order.status)} onClick={() => setCameraAction("arrived")}>
                  <Camera className="mr-2 h-4 w-4" /> {t("arrivedBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || !["arrived", "driving"].includes(order.status)} onClick={() => startWaiting()}>
                  <Clock className="mr-2 h-4 w-4" /> {order.status === "driving" ? t("resumeWaitingBtn") : t("startWaitingBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || order.status !== "waiting"} onClick={() => runAction("start-driving")}>
                  <Navigation className="mr-2 h-4 w-4" /> {t("pauseDrivingBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || !["arrived", "waiting", "driving"].includes(order.status)} onClick={() => setCameraAction("collected")}>
                  <Check className="mr-2 h-4 w-4" /> {t("collectedBtn")}
                </Button>
                <Button className="h-12 text-base" disabled={isLoading || order.status !== "collected"} onClick={() => runAction("returned-to-branch")}>
                  <Navigation className="mr-2 h-4 w-4" /> {t("returnedToBranchBtn")}
                </Button>
                <Button className="h-12 text-base" variant="destructive" disabled={isLoading || !["arrived", "waiting", "driving"].includes(order.status)} onClick={() => setCameraAction("no-show")}>
                  <XCircle className="mr-2 h-4 w-4" /> {t("markNoShowBtn")}
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
